import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "../Cards/Card";
import ComboHeader from "../ComboHeader/ComboHeader";
import { Box, Grid } from "@mui/material";
import ObjectLoader from "../ObjectLoader/ObjectLoader";
import ProgressBar from "../ProgressBar/ProgressBar";
import { useSelections } from "../../context/Selections";
import Kpi from "../KPI/Kpi";

function KpiCardOverview({
  app,
  KPITitle,
  tooltipText,
  percentageId,
  valueId,
  valueDateId,
  goalValueId,
  goalDateId,
  hasProgressBar,
  fullView,
}) {
  const [percentageKPI, setPercentage] = useState(null);
  const [loadingCard, setLoadingCard] = useState(true);
  const { selections } = useSelections();

  let [value, goalValue, valueDate, goalDate] = [
    Kpi({
      app,
      objectId: valueId,
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: goalValueId,
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: valueDateId,
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: goalDateId,
      updatesWithSelection: true,
    }),
  ];

  const getGoalValueColor = (goalValue) => {
    const goalValueNumber = parseFloat(goalValue);
    if (goalValueNumber < 50) {
      return "#E95248";
    } else if (goalValueNumber < 90) {
      return "#FDB949";
    } else {
      return "#38BC9A";
    }
  };

  const goalValueColor = getGoalValueColor(goalValue);

  const getColorFromPercentage = (percentage) => {
    switch (true) {
      case percentage < 0.5:
        return "#E95248";
      case percentage < 0.9:
        return "#FDB949";
      default:
        return "#38BC9A";
    }
  };

  const fetchKPIValue = (objectId) => {
    if (app) {
      let id = objectId;
      setLoadingCard(true);
      setTimeout(() => {
        return fullView
          ? app?.visualization?.get(id).then((res) => {
              const valueInfo =
                res.model.layout.qHyperCube?.qGrandTotalRow[0] ?? null;
              setPercentage({
                text: valueInfo?.qText,
                value: valueInfo?.qNum,
                color: getColorFromPercentage(valueInfo?.qNum),
              });
            })
          : setLoadingCard(false);
      }, 500);
    }
  };

  useEffect(() => {
    if (app) {
      fetchKPIValue(percentageId);
    }
  }, [selections, fullView]);

  useEffect(() => {
    if (percentageKPI) {
      setLoadingCard(false);
    }
  }, [percentageKPI]);

  return (
    <Card
      app={app}
      key={KPITitle}
      style={{
        width: "12.5%",
        height: "77px",
        paddingLeft: "16px",
        paddingRight: "16px",
      }}
    >
      {loadingCard ? (
        <ObjectLoader
          icon="circle"
          width={"100%"}
          height={"100%"}
          className="circle-wrapper"
        />
      ) : (
        <Grid container className="kpi-wrapper">
          <Grid
            container
            flexDirection={"row"}
            justifyContent={"flex-start"}
            alignItems={"flex-start"}
          >
            <Grid
              item
              style={{
                width: "70%",
                display: "flex",
              }}
            >
              {/* TITULO */}
              <ComboHeader
                reference={{
                  title: {
                    hard: KPITitle,
                  },
                }}
                tooltip={tooltipText ?? null}
              />
            </Grid>
          </Grid>
          <Grid
            className={`value-wrapper${hasProgressBar ? "" : "-2"}`}
            item
            xs={12}
            flexDirection={"column-reverse"}
            justifyContent={"flex-start"}
            alignItems={"flex-start"}
          >
            {hasProgressBar && (
              <Grid
                container
                flexDirection={"column-reverse"}
                justifyContent={"flex-end"}
                alignItems={"flex-start"}
                style={{
                  width: "100%",
                  height: "30%",
                  display: "flex",
                  minWidth: "110px",
                }}
              >
                {/* PROGRESS */}
                {fullView ? (
                  percentageKPI && (
                    <>
                      <div className="container-progress-bar">
                        <ProgressBar
                          key={percentageKPI}
                          bgColor={percentageKPI.color}
                          completed={percentageKPI.value * 100}
                        />
                      </div>
                    </>
                  )
                ) : (
                  <ProgressBar key={0} bgColor={"grey"} completed={0} />
                )}
              </Grid>
            )}

            <Grid
              container
              flexDirection={"row"}
              justifyContent={"flex-start"}
              alignItems={"flex-end"}
              style={{
                marginTop: hasProgressBar ? "0" : "16px",
                width: "100%",
                height: "70%",
              }}
            >
              <Grid
                item
                className="value"
                flexDirection={"column-reverse"}
                justifyContent={"flex-end"}
                alignItems={"flex-end"}
                style={{
                  height: "100%",
                }}
              >
                {/* MAIN VALUE */}
                <Box
                  className="contianer-porcente-title"
                  component={"span"}
                  display={"flex"}
                  alignItems={"center"}
                  gap={"2px"}
                  sx={{
                    color:
                      hasProgressBar && fullView
                        ? percentageKPI?.color
                        : "#666",
                  }}
                  fontSize={"18px"}
                >
                  {/* {value} */}
                  {percentageKPI?.text}{" "}
                  <Box component={"span"} fontSize={"12px"} color={"#A3A3A3"}>
                    Atingimento
                  </Box>
                </Box>
              </Grid>

              {!hasProgressBar && (
                <Grid
                  item
                  className="value-date"
                  flexDirection={"row"}
                  justifyContent={"flex-start"}
                  alignItems={"flex-end"}
                  style={{}}
                >
                  {/* VALUE DATE */}
                  <Box component={"span"}>{valueDate}</Box>
                </Grid>
              )}
            </Grid>
          </Grid>
          {fullView ? (
            <Grid
              item
              flexDirection={"row"}
              justifyContent={"flex-start"}
              alignItems="flex-end"
              style={{
                width: "100%",
                height: "30%",
                display: "flex",
              }}
            >
              {/* GOAL VALUE */}
            </Grid>
          ) : (
            <span className="filter-warning">
              Selecione apenas 1 Mês/Ano para análise com meta
            </span>
          )}
        </Grid>
      )}
    </Card>
  );
}

KpiCardOverview.defaultProps = {
  app: null,
  KPITitle: "",
  tooltipText: "",
  percentageId: null,
  valueId: null,
  valueDateId: null,
  goalValueId: null,
  goalDateId: null,
  hasProgressBar: false,
  fullView: true,
};

KpiCardOverview.propTypes = {
  app: PropTypes.object,
  KPITitle: PropTypes.string,
  tooltipText: PropTypes.string,
  percentageId: PropTypes.string,
  valueId: PropTypes.string,
  valueDateId: PropTypes.string,
  goalValueId: PropTypes.string,
  goalDateId: PropTypes.string,
  hasProgressBar: PropTypes.bool,
  fullView: PropTypes.bool,
};

export default KpiCardOverview;
